import {
  type ComponentType,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  type SVGProps,
  useEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import Button from '@/components/Button';
import IcCross from '@/assets/icons/Cross.svg?react';
import styles from './GlobalAlertDialog.module.scss';

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;
export type AlertDialogIconVariant = 'brand' | 'danger' | 'warning' | 'success';
export type AlertDialogAcceptVariant = 'primary' | 'alert';

export interface AlertDialogData {
  acceptLabel?: string;
  acceptVariant?: AlertDialogAcceptVariant;
  closeOnDecline?: boolean;
  closeOnOption?: boolean;
  closeOnAccept?: boolean;
  declineLabel?: string;
  desc?: string;
  hideDeclineButton?: boolean;
  icon?: IconComponent;
  iconVariant?: AlertDialogIconVariant;
  // Entity summary line rendered below the description, e.g.
  //   "Mira Lehmann · mira@example.com" — name bold, sub muted.
  entityLabel?: string;
  entitySublabel?: string;
  optionLabel?: string;
  onAccept?: () => void | Promise<void>;
  onDecline?: () => void | Promise<void>;
  onOption?: () => void | Promise<void>;
  preventClose?: boolean;
  reactContent?: ReactNode | ReactNode[];
  title?: string;
}

interface AlertDialogProps extends AlertDialogData {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

const GlobalAlertDialog = ({
  acceptLabel,
  acceptVariant = 'alert',
  closeOnDecline = true,
  closeOnOption = true,
  closeOnAccept = true,
  declineLabel,
  desc,
  entityLabel,
  entitySublabel,
  hideDeclineButton = false,
  icon: Icon,
  iconVariant = 'brand',
  onAccept,
  onDecline,
  onOption,
  optionLabel,
  preventClose = false,
  reactContent,
  title,
  isOpen,
  setIsOpen,
}: AlertDialogProps) => {
  const { t } = useTranslation(['common']);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [optionLoading, setOptionLoading] = useState<boolean>(false);
  const [acceptLoading, setAcceptLoading] = useState<boolean>(false);
  const [reactContentKey, setReactContentKey] = useState(0);
  const [prevReactContent, setPrevReactContent] = useState(reactContent);

  if (prevReactContent !== reactContent) {
    setPrevReactContent(reactContent);
    setReactContentKey((k) => k + 1);
  }

  const handleClose = () => {
    if (preventClose || optionLoading || acceptLoading) {
      return;
    }
    if (dialogRef.current?.open) {
      void onDecline?.();

      if (closeOnDecline) {
        setIsOpen(false);
      }
    }
  };

  const handleOption = async () => {
    if (onOption) {
      setOptionLoading(true);

      try {
        await onOption();
      } finally {
        setOptionLoading(false);
      }
    }

    if (closeOnOption) {
      setIsOpen(false);
    }
  };

  const handleAccept = async () => {
    if (onAccept) {
      setAcceptLoading(true);

      try {
        await onAccept();
      } finally {
        setAcceptLoading(false);
      }
    }

    if (closeOnAccept) {
      setIsOpen(false);
    }
  };

  const handleEscapeKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleClose();
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      event.offsetX < 0 ||
      event.offsetX > (event.target as HTMLElement).offsetWidth ||
      event.offsetY < 0 ||
      event.offsetY > (event.target as HTMLElement).offsetHeight
    ) {
      handleClose();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleEscapeKey, false);

    return () => {
      document.removeEventListener('keydown', handleEscapeKey, false);
    };
  }, [handleEscapeKey]);

  useEffect(() => {
    const dialog = dialogRef.current;
    dialog?.addEventListener('click', handleClickOutside);

    return () => {
      dialog?.removeEventListener('click', handleClickOutside);
    };
  }, [dialogRef, handleClickOutside, handleClose]);

  useEffect(() => {
    if (isOpen) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [isOpen]);

  const useCompactLayout = !!Icon;

  return createPortal(
    <dialog
      className={classNames(styles.dialog, {
        [styles.dialogCompact]: useCompactLayout,
      })}
      ref={dialogRef}
    >
      {(!preventClose || onDecline) && (
        <button
          type="button"
          className={styles.close}
          onClick={preventClose ? onDecline : handleClose}
          disabled={optionLoading || acceptLoading}
          aria-label={t('common:dialog.decline')}
        >
          <IcCross className={styles.icon} />
        </button>
      )}
      {Icon && (
        <div
          className={classNames(
            styles.iconBadge,
            styles[`tint_${iconVariant}`]
          )}
        >
          <Icon className={styles.iconBadgeIcon} aria-hidden="true" />
        </div>
      )}
      <h1 className={styles.title}>
        {title || t('common:dialog.titleDelete')}
      </h1>
      {desc && <div className={styles.description}>{desc}</div>}
      {(entityLabel || entitySublabel) && (
        <div className={styles.entity}>
          {entityLabel && (
            <span className={styles.entityLabel}>{entityLabel}</span>
          )}
          {entityLabel && entitySublabel && (
            <span className={styles.entitySeparator} aria-hidden="true">
              {' · '}
            </span>
          )}
          {entitySublabel && (
            <span className={styles.entitySublabel}>{entitySublabel}</span>
          )}
        </div>
      )}
      {reactContent && <div key={reactContentKey}>{reactContent}</div>}
      <div
        className={classNames(styles.buttons, {
          [styles.buttonsCompact]: useCompactLayout,
        })}
      >
        {!hideDeclineButton && (!preventClose || onDecline) && (
          <Button
            variant="secondary"
            size={useCompactLayout ? 'slim' : 'regular'}
            className={styles.cancel}
            onClick={preventClose ? onDecline : handleClose}
            disabled={optionLoading || acceptLoading}
          >
            {declineLabel || t('common:dialog.decline')}
          </Button>
        )}
        {optionLabel && onOption && (
          <Button
            className={styles.option}
            size={useCompactLayout ? 'slim' : 'regular'}
            onClick={handleOption}
            variant="primary"
            disabled={acceptLoading}
            loading={optionLoading}
          >
            {optionLabel}
          </Button>
        )}
        {onAccept && (
          <Button
            className={styles.accept}
            size={useCompactLayout ? 'slim' : 'regular'}
            onClick={handleAccept}
            variant={acceptVariant}
            disabled={optionLoading}
            loading={acceptLoading}
          >
            {acceptLabel || t('common:dialog.accept')}
          </Button>
        )}
      </div>
    </dialog>,
    document.getElementById('alertDialog')!
  );
};

export default GlobalAlertDialog;
